import { ulMessageCheckSquare } from '@faceup/icons/ulMessageCheckSquare'
import { ulPlus } from '@faceup/icons/ulPlus'
import { ulSearchSm } from '@faceup/icons/ulSearchSm'
import { defineMessages, FormattedMessage } from '@faceup/localization'
import { Institution, type Plan } from '@faceup/utils'
import { useAccessKey } from '../App/AccessKeyProvider'
import { useAppLock } from '../App/AppLockProvider'
import { useReportSource } from '../App/ReportSourceProvider'
import type { MenuItem } from '../Components/MenuItems/MenuItems'

import { useAnalytics } from '../utils/analytics'
import { useIsCheckReportAvailable } from './useIsCheckReportAvailable'

const messages = defineMessages('followUp.hooks.useReportMenuItems', [
  { id: 'createReport', defaultMessage: 'Send a report' },
  { id: 'createReportForTestingSchool', defaultMessage: 'Send test report' },
  { id: 'backToSchoolList', defaultMessage: 'Back to searching for school' },
  { id: 'checkReport', defaultMessage: 'Check a report' },
] as const)

type UseReportMenuItemsParams = {
  plan: Plan | null | undefined
}

export const useReportMenuItems = ({ plan }: UseReportMenuItemsParams): MenuItem[] => {
  const { isCheckReportAvailable } = useIsCheckReportAvailable({ plan })
  const { logout } = useAppLock()
  const { clearAccessKey } = useAccessKey()
  const reportSource = useReportSource()
  const { track } = useAnalytics()

  const isTestingSchool =
    reportSource?.isTesting === true && reportSource.type === Institution.School
  const isReportFormAvailable = reportSource?.isEnabled !== false

  return [
    ...(isReportFormAvailable
      ? [
          {
            key: 'createReport',
            url: routes => routes.createReport(),
            onClick: () => track('create_report_button_clicked'),
            icon: ulPlus,
            label: isTestingSchool ? (
              <FormattedMessage message={messages.createReportForTestingSchool} />
            ) : (
              <FormattedMessage message={messages.createReport} />
            ),
            variant: 'filled',
            dataTest: 'menu-item-create',
          } satisfies MenuItem,
        ]
      : []),
    ...(isCheckReportAvailable
      ? [
          {
            key: 'checkReport',
            url: routes => routes.checkReport(),
            icon: ulMessageCheckSquare,
            label: <FormattedMessage message={messages.checkReport} />,
            dataTest: 'menu-item-check',
          } satisfies MenuItem,
        ]
      : []),
    ...(isTestingSchool
      ? [
          {
            key: 'logout',
            // Leaving a testing school must return to search. The pure
            // InstitutionLayout has no redirect guard, so navigate home and drop
            // the in-memory access key synchronously (before navigation) so
            // HomeRedirect lands on SearchInstitution instead of re-entering.
            url: routes => routes.home(),
            onClick: () => {
              clearAccessKey()
              void logout()
            },
            icon: ulSearchSm,
            label: <FormattedMessage message={messages.backToSchoolList} />,
          } satisfies MenuItem,
        ]
      : []),
  ]
}
